import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// i18next
import i18n from 'i18n'
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { getResult } from 'store/player/playerAsync';
import { setQuiz } from 'store/quiz/quizSlice';
import { selectResult } from 'store/player/playerSelectors';
import { selectQuiz } from 'store/quiz/quizSelectors';
import IQuiz from 'models/Quiz';
import QuizTypes from 'types/QuizTypes';
// MUI
import { Box } from '@mui/material';
// Components
import AppLoader from 'components/AppLoader';
import AppBar from './AppBar';
// Utilities
import { decodeBase64, getSearchParam } from 'utilities/Utilities';
import ResultsList from './ResultsList';
import Prizes from './Prizes';
import QrCode from './QrCode';

let interval: any;
const limit = 10;

const LiveLeaderboardPage:FC = () => {
  const { slug } = useParams<{ slug:string }>();
  const { groupSlug } = useParams<{ groupSlug:string }>();

  const { search } = window.location;
  const hasHash = getSearchParam(search, 'hasHash');

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const storeQuiz = useAppSelector(selectQuiz);

  const onReceiveMessage = (event:any) => {
    const { action, quizHash } = event.data;
    if ( action === 'preview' && quizHash ){
      dispatch(setQuiz(decodeBase64(quizHash)))
    }
  }

  useEffect(() => {
    window.addEventListener('message', onReceiveMessage, false);

    return () => {
      window.removeEventListener('message', onReceiveMessage, false);
    }
    // eslint-disable-next-line
  }, []);

  const result:any = useAppSelector(selectResult);
  const quiz:IQuiz | null = storeQuiz || result?.quiz || null;
  const quizGroup:any = result?.quizGroup || null;
  const [loading, setLoading] = useState<boolean>(false);

  const fetchResult = (isTimer: boolean) => {
    if (groupSlug) {
      const data:any = { quizGroupName: groupSlug, limit };
      if (hasHash) data['liveLeaderboardPreview'] = true;
      dispatch(getResult(data)).unwrap()
        .catch(() => {
          if (!isTimer) {
            navigate('/404-not-found');
          }
        })
        .finally(() => setLoading(false));
    }
    if (slug) {
      const data:any = { quizName: slug, limit };
      if (hasHash) data['liveLeaderboardPreview'] = true;
      dispatch(getResult(data)).unwrap()
      .catch(() => {
        if (!isTimer) {
          navigate('/404-not-found');
        }
      })
      .finally(() => setLoading(false));
    }
  }

  useEffect(() => {
    setLoading(true);
    fetchResult(false);

    if (!hasHash) {
      interval = setInterval(() => { fetchResult(true) }, 10000);
    }

    const rootDiv = document.getElementById('root');
    rootDiv?.classList.add('liveLeaderboardMode');

    return () => {
      if (interval) clearInterval(interval);
      rootDiv?.classList.remove('liveLeaderboardMode');
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!quiz) return;
    if (![QuizTypes.Scoring, QuizTypes.Prediction].includes(quiz.type)) navigate('/404-not-found');
    if (quiz.type === QuizTypes.Prediction && quiz.numberOfQuestions === quiz.numberOfAnsweredQuestions) {
      clearInterval(interval);
    }
    document.documentElement.style.setProperty('--primaryColor', quiz.theme.primaryColor);
    i18n.changeLanguage(quiz.language || 'en');
    // eslint-disable-next-line
  }, [quiz]);

  useEffect(() => {
    if (!quizGroup) return;
    document.documentElement.style.setProperty('--primaryColor', quizGroup.theme.primaryColor);
  }, [quizGroup]);

  const bgImageUrl = quiz ? quiz.publicLeaderboard.wallpaperImage : quizGroup ? quizGroup.publicLeaderboard.wallpaperImage : '';
  const portraitBgImageUrl = quiz ? quiz.publicLeaderboard.portraitWallpaperImage : quizGroup ? quizGroup.publicLeaderboard.portraitWallpaperImage : '';

  const textColor = quiz? quiz.publicLeaderboard.textColor : quizGroup ? quizGroup.publicLeaderboard.textColor : '#fff';
  
  if (loading || !result) return <AppLoader />;
  return (
    <Box
      className="h-full overflow-scroll px-4 py-2 bg-cover bg-center bg-black sm:px-8 sm:py-4 xl:bg-right-top"
      sx={{
        color: textColor,
        '@media screen and (orientation: portrait)': {
          backgroundImage: `url(${portraitBgImageUrl || bgImageUrl})`,
        },
        '@media screen and (orientation: landscape)': {
          backgroundImage: `url(${bgImageUrl || portraitBgImageUrl})`,
        }
      }}
    >
      <AppBar />
      <div className="flex flex-col xl:flex-row portrait:flex-col gap:4 sm:gap-12">
        <div className="flex-1 px-4 sm:px-8 ">
          <ResultsList />
          <Prizes />
        </div>
        <div className="flex-1 px-4 sm:px-8">
          <QrCode />
        </div>
      </div>
    </Box>
  );
}

export default LiveLeaderboardPage;
